import * as Castle from '@castleio/castle-js';
import { datadogRum } from '@datadog/browser-rum';
import { ExtraErrorData } from '@sentry/integrations';
import { init, SeverityLevel } from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import axios from 'axios';

import BrowserPush from '@/BrowserPush';

import RedirectTo from './components/Common/RedirectTo';
import RestAPI from './components/Common/restApi';

declare global {
  interface Window {
    SENTRY?: {
      enabled: boolean;
      environment: string;
    };
    CASTLE?: {
      enabled: boolean;
      public_api_key: string;
    };
    SEGMENT?: {
      enabled: boolean;
      jsKey: string;
    };
    FIREBASE_CONFIGURATION?: {
      apiKey: string;
      appId: string;
      authDomain: string;
      databaseURL: string;
      messagingSenderId: string;
      projectId: string;
      storageBucket: string;
    };
    FIREBASE_PUBLIC_VAPID_KEY?: string;
  }
}

function isValidHttpUrl(value?: string) {
  let url;

  try {
    url = new URL(value!);
  } catch (error) {
    return false;
  }

  return url.protocol === 'http:' || url.protocol === 'https:';
}

function isInput(target?: HTMLElement): target is HTMLInputElement {
  return target?.nodeName.toLowerCase() === 'input';
}

function getBreadCrumbMessage(target: HTMLElement | undefined | HTMLInputElement) {
  const textContent = target?.innerText;

  return isInput(target) ? target?.labels?.[0]?.textContent : textContent;
}

const breadCrumbCategories = ['ui.click', 'ui.input'];

export const setupSentry = () => {
  if (window.SENTRY?.enabled) {
    init({
      ignoreErrors: [
        // Random plugins/extensions
        'top.GLOBALS',
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'http://tt.epicplay.com',
        "Can't find variable: ZiteReader",
        'jigsaw is not defined',
        'ComboSearch is not defined',
        'http://loading.retry.widdit.com/',
        'atomicFindClose',
        // Facebook borked
        'fb_xd_fragment',
        // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        'conduitPage',
        // Generic error code from errors outside the security sandbox
        // You can delete this if using raven.js > 1.0, which ignores these automatically.
        'Script error.',
        'fromLatLngToDivPixel',
        'lineItemType',
        "Can't find variable: analytics",
      ],
      allowUrls: [/.*housecallpro.*/],
      debug: process.env.NODE_ENV !== 'production',
      integrations: (integrations) => [
        ...integrations,
        new ExtraErrorData({
          depth: 10,
        }),
        new Integrations.BrowserTracing({
          tracePropagationTargets: [
            'localhost:3000',
            'https://pro.housecallpro-staging.com',
            'https://pro.housecallpro.com',
          ],
        }),
      ],
      tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
      release: process.env.REVISION,
      normalizeDepth: 11,
      dsn: 'https://b7ffb5e5f2a84a1893cb49f67f60c35c@o284669.ingest.sentry.io/6006256',
      environment: window.SENTRY?.environment,
      beforeBreadcrumb(breadcrumb, hint) {
        try {
          if (breadcrumb.message?.includes('bam.nr-data.net')) {
            return null;
          }

          if (breadcrumb.category && breadCrumbCategories.includes(breadcrumb.category)) {
            const target = hint?.event.target as HTMLElement | undefined;

            return {
              ...breadcrumb,
              level: 'debug' as SeverityLevel,
              message: getBreadCrumbMessage(target) ?? breadcrumb.message,
              data: {
                className: target?.className,
                htmlTag: target?.nodeName,
                id: target?.id,
                dataTestId: target?.getAttribute('data-testid'),
              },
            };
          }

          return breadcrumb;
        } catch (_err) {
          return breadcrumb;
        }
      },
    });
  }
};

export const setupRestApi = () => {
  RestAPI.onPaymentRequired(() => {
    RedirectTo.accountNoAccess();
  });
};

const getDeviceContext = () => {
  const deviceContext = JSON.stringify({
    os: {
      name: 'Web',
    },
    userAgent: navigator.userAgent,
  });

  return btoa(deviceContext);
};

export const addAmplitudeToConfigHeader = () => {
  axios.interceptors.request.use((config) => {
    if (!isValidHttpUrl(config.url)) {
      // eslint-disable-next-line no-underscore-dangle
      const sessionId = window.amplitude?.getInstance()?._sessionId;

      Object.assign(config.headers?.common ?? {}, {
        'Segment-Device-Context': getDeviceContext(),
        ...(sessionId ? { 'Amplitude-Session-Id': sessionId } : {}),
      });
    }

    return config;
  });
};

export const setupCastle = () => {
  if (window.CASTLE?.enabled) {
    Castle.configure({ pk: window.CASTLE?.public_api_key });
  }
};

export const setupBrowserPush = () => {
  BrowserPush.monitor(window.FIREBASE_CONFIGURATION, window.FIREBASE_PUBLIC_VAPID_KEY);
};

export const isProdOrStaging = () => process.env.HC_ENV === 'production' || process.env.HC_ENV === 'staging';

export const setupDataDog = () => {
  if (isProdOrStaging()) {
    datadogRum.init({
      applicationId: 'f8ff5369-f5d9-4668-be05-b573bbebb36b',
      clientToken: 'pub60663f73b4d2047d964a544f40f580c6',
      env: process.env.HC_ENV,
      version: process.env.REVISION,
      site: 'datadoghq.com',
      service: 'housecall-web',

      sessionSampleRate: 100,
      sessionReplaySampleRate: 100, // if not included, the default is 100

      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      trackFrustrations: true,

      enableExperimentalFeatures: ['feature_flags'],
      /* Setting defaultPrivacyLevel to mask mode will mask all HTML text, user input, images, and links.
        Text on your application is replaced with X, rendering the page into a wireframe. */
      defaultPrivacyLevel: 'mask-user-input',
      actionNameAttribute: 'data-dd-action-name',
    });
    datadogRum.startSessionReplayRecording();
  }
};
